import React from 'react';
import { useDynamicYear } from '../../hooks/useDynamicYear';

/**
 * DynamicYear component for displaying year information
 * @param {Object} props - Component props
 * @param {string} props.type - Type of year display ('current', 'range', 'academic', 'fiscal')
 * @param {number} props.startYear - Start year for ranges
 * @param {number} props.endYear - End year for ranges
 * @param {string} props.format - Display format ('text', 'copyright', 'academic', 'fiscal')
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showLabel - Whether to show a label
 * @returns {JSX.Element} Dynamic year component
 */
const DynamicYear = ({ 
  type = 'current', 
  startYear, 
  endYear, 
  format = 'text',
  className = '',
  showLabel = false 
}) => {
  const { currentYear, yearString, getYearRangeString, getAcademicYearString, getFiscalYearString } = useDynamicYear({
    type,
    startYear,
    endYear
  });

  const getDisplayText = () => {
    switch (format) {
      case 'copyright':
        return `© ${yearString}`;
      case 'academic':
        return getAcademicYearString(currentYear);
      case 'fiscal':
        return getFiscalYearString(currentYear);
      case 'range':
        return getYearRangeString(startYear, endYear);
      case 'text':
      default:
        return yearString;
    }
  };

  const getLabel = () => {
    switch (type) {
      case 'academic':
        return 'Academic Year: ';
      case 'fiscal':
        return 'Fiscal Year: ';
      case 'range':
        return 'Year Range: ';
      default:
        return '';
    }
  };

  return (
    <span className={className}>
      {showLabel && getLabel()}
      {getDisplayText()}
    </span>
  );
};

export default DynamicYear;
